import React, { useMemo, useState, useEffect, useCallback } from "react";
import { GetAllRoomsIdList, GetAllRoomsWithFloor } from "adminApi/AssignRoomApi/AssignRoom";
import useFetchAllData from "Hooks/useFetchAllData";

const RoomCheckboxGroup = ({ values, setValues }) => {
    const [loadingAssigned, setLoadingAssigned] = useState(false);
    const [selectedRooms, setSelectedRooms] = useState([]);
    const [checkbox, setCheckbox] = useState([]);

    // `loading` not `isLoading` — matches useFetchAllData return shape
    const { data: allRoomsWithFloor, loading, error } = useFetchAllData(GetAllRoomsWithFloor);

    // Stable map from room_no → floor_assign_id
    const roomNoToFloorAssignId = useMemo(() => {
        const map = new Map();
        (allRoomsWithFloor || []).forEach((r) => {
            if (r.room_no != null) map.set(String(r.room_no), r.floor_assign_id);
        });
        return map;
    }, [allRoomsWithFloor]);

    // Fetch assigned rooms for the selected room type
    // Depends only on room_type — not on allRoomsWithFloor to avoid re-fetching on every room list reload
    const fetchSelectedRooms = useCallback(async (roomTypeId, roomsWithFloor) => {
        setLoadingAssigned(true);
        try {
            const res = await GetAllRoomsIdList(roomTypeId);
            const data = res?.data?.data || res?.data || [];
            setSelectedRooms(data || []);

            // Resolve floor_assign_ids from the API response
            const ids = (data || []).map((item) => {
                if (item.floor_assign_id) return item.floor_assign_id;
                if (item.room_id) {
                    const found = (roomsWithFloor || []).find((r) => r.room_id === item.room_id);
                    if (found) return found.floor_assign_id;
                }
                if (item.room_no != null) {
                    const mapped = roomNoToFloorAssignId.get(String(item.room_no));
                    if (mapped) return mapped;
                }
                return null;
            }).filter(Boolean);

            setCheckbox(ids);
            setValues((prev) => ({ ...prev, room_no: ids }));
        } catch (err) {
            console.error("Error fetching selected rooms:", err);
        } finally {
            setLoadingAssigned(false);
        }
    }, [roomNoToFloorAssignId, setValues]);

    // Fetch assigned rooms when room_type changes — wait for allRoomsWithFloor to be ready
    useEffect(() => {
        if (!values?.room_type) {
            setSelectedRooms([]);
            setCheckbox([]);
            setValues((prev) => ({ ...prev, room_no: [] }));
            return;
        }

        // Only fetch once allRoomsWithFloor is loaded so ID resolution works correctly
        if (!allRoomsWithFloor?.length) return;

        fetchSelectedRooms(values.room_type, allRoomsWithFloor);
    }, [values?.room_type, allRoomsWithFloor?.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Group rooms by floor for display
    const groupedRooms = useMemo(() => {
        if (!allRoomsWithFloor?.length) return [];
        return Object.entries(
            allRoomsWithFloor.reduce((acc, room) => {
                const title = room.floor_name_title || `Floor ${room.floor_name}`;
                (acc[title] ||= []).push(room);
                return acc;
            }, {})
        ).map(([floor_name_title, rooms]) => ({ floor_name_title, rooms }));
    }, [allRoomsWithFloor]);

    const onCheckboxChange = (floorAssignId) => () => {
        setCheckbox((prev) => {
            const updated = prev.includes(floorAssignId)
                ? prev.filter((id) => id !== floorAssignId)
                : [...prev, floorAssignId];
            setValues((prevVals) => ({ ...prevVals, room_no: updated }));
            return updated;
        });
    };

    if (loading || loadingAssigned)
        return <p className="text-muted text-center py-4 fw-semibold">Loading rooms...</p>;
    if (error)
        return <p className="text-danger text-center py-4 fw-semibold">Failed to load rooms.</p>;
    if (!groupedRooms.length)
        return <p className="text-muted text-center py-4 fw-semibold">No rooms found.</p>;

    return (
        <div className="floor-room-checkbox-group mt-4">
            {groupedRooms.map(({ floor_name_title, rooms }) => {
                const availableRooms = rooms.filter((r) => !r.assigned_room_no);
                const unavailableRooms = rooms.filter((r) => r.assigned_room_no);

                return (
                    <div key={floor_name_title} className="card border-0 shadow-sm mb-4 rounded-3 overflow-hidden user-select-none">
                        {/* Floor Header */}
                        <div className="card-header border-0 d-flex justify-content-between align-items-center py-2 mx-2 gap-3 border-bottom">
                            <h6 className="mb-0 fw-semibold d-flex align-items-center">
                                <i className="fa fa-building-o me-2 text-secondary" />
                                {floor_name_title}
                            </h6>
                            <span className="border rounded px-2 py-1 small">
                                Total Rooms: {rooms.length}
                            </span>
                        </div>

                        {/* Room Lists */}
                        <div className="card-body">
                            <div className="row g-4">
                                {/* Available Rooms */}
                                <div className="col-md-6">
                                    <div className="p-3 rounded bg-success-20">
                                        <h6 className="text-success fw-semibold mb-3 d-flex align-items-center">
                                            <i className="fa fa-check-circle me-2" /> Available Rooms
                                        </h6>
                                        {availableRooms.length ? (
                                            <div className="d-flex flex-wrap gap-3">
                                                {availableRooms.map((room) => (
                                                    <div
                                                        key={room.floor_assign_id}
                                                        className="form-check d-flex align-items-center border rounded-3 bg-light px-3 py-2"
                                                    >
                                                        <input
                                                            id={`room-${room.floor_assign_id}`}
                                                            type="checkbox"
                                                            className="form-check-input me-2 mt-0"
                                                            value={room.floor_assign_id}
                                                            checked={checkbox.includes(room.floor_assign_id)}
                                                            onChange={onCheckboxChange(room.floor_assign_id)}
                                                        />
                                                        <label
                                                            htmlFor={`room-${room.floor_assign_id}`}
                                                            className="form-check-label small fw-medium text-secondary mb-0"
                                                        >
                                                            Room No{" "}
                                                            <span className="fw-semibold text-success d-block">
                                                                {room.room_no}
                                                            </span>
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-muted small fst-italic">No available rooms.</p>
                                        )}
                                    </div>
                                </div>

                                {/* Unavailable Rooms */}
                                <div className="col-md-6">
                                    <div className="p-3 rounded bg-danger-20">
                                        <h6 className="text-danger fw-semibold mb-3 d-flex align-items-center">
                                            <i className="fa fa-times-circle me-2" /> Unavailable Rooms
                                        </h6>
                                        {unavailableRooms.length ? (
                                            <div className="d-flex flex-wrap gap-3">
                                                {unavailableRooms.map((room) => {
                                                    const isSameType =
                                                        room.room_type === values?.room_type ||
                                                        selectedRooms.some((r) => {
                                                            if (r.room_id && r.room_id === room.room_id) return true;
                                                            if (r.floor_assign_id && r.floor_assign_id === room.floor_assign_id) return true;
                                                            if (r.room_no != null && String(r.room_no) === String(room.room_no)) return true;
                                                            return false;
                                                        });

                                                    return (
                                                        <div
                                                            key={`un-${room.floor_assign_id}`}
                                                            className={`form-check d-flex align-items-center border rounded-3 px-3 py-2 ${isSameType ? "bg-light" : "bg-light opacity-75"}`}
                                                        >
                                                            <input
                                                                id={`un-${room.floor_assign_id}`}
                                                                type="checkbox"
                                                                className={`form-check-input me-2 mt-0 ${isSameType ? "bg-purple" : ""}`}
                                                                value={room.floor_assign_id}
                                                                checked={checkbox.includes(room.floor_assign_id)}
                                                                disabled={!isSameType}
                                                                onChange={isSameType ? onCheckboxChange(room.floor_assign_id) : undefined}
                                                            />
                                                            <label
                                                                htmlFor={`un-${room.floor_assign_id}`}
                                                                className={`form-check-label small fw-medium mb-0 ${isSameType ? "text-secondary" : "text-muted text-decoration-line-through"}`}
                                                            >
                                                                Room No{" "}
                                                                <span className="fw-semibold text-danger d-block">
                                                                    {room.room_no}
                                                                </span>
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-muted small fst-italic">No assigned rooms.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default RoomCheckboxGroup;
